

import React, { useEffect, useState, useRef } from "react";
import WorkspaceService from "services/workspace-service";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import SelectPrimaryProfile from "../SelectOwnProfile";
import SelectSecondaryProfile from "../SelectCompetitorProfile";
import SocialPopup from "../SocialPopup";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import IconButton from "@mui/material/IconButton";
import ConnectPrimaryProfile from "layouts/components/ConnectOwnProfile";
import ReviewAndGenerateSnapshot from "../ReviewAndGenerateSnapshot";
import UserService from "services/user-service";
import SnapshotService from "services/snapshot-service";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
// import SnapshotLoading from "../SnapshotLoading"; // [DELETE]
import CircularProgress from "@mui/material/CircularProgress";
import SnapshotStepper from "../Stepper/SnapshotStepper";
import MDSnackbar from "components/MDSnackbar";

dayjs.extend(utc);

function BrandSnapshot({ goBack, onComplete, editSnapshotData = null, entitlements, isLiveDemo, showGuidance }) {
  const [selectedPrimaryProfile, setSelectedPrimaryProfile] = useState(null);
  const [selectedSecondaryProfiles, setSelectedSecondaryProfiles] = useState([]);
  const [step, setStep] = useState(1);
  const [showConnectDialog, setShowConnectDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [errorSB, setErrorSB] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showEmailError, setShowEmailError] = useState(false); // [NEW] Inline error state

  const openErrorSB = (msg) => {
    setErrorMessage(msg);
    setErrorSB(true);
  };
  const closeErrorSB = () => setErrorSB(false);
  const snapshotConfigRef = useRef({});
  const primaryProfileRef = useRef();

  // [NEW] Combined flag for highlights
  const enableDemoHighlight = isLiveDemo && showGuidance;

  const [navHeight, setNavHeight] = useState(75);
  const [isTransparent, setIsTransparent] = useState(true);

  // ───────────────────────────────
  // Dynamic Step Labels
  // ───────────────────────────────
  const steps = [
    "Select Snapshot Type",
    "Select Primary Profile",
    "Select Secondary Profiles",
    isEditing ? "Review & Save Snapshot" : "Review & Save Snapshot",
  ];

  // ───────────────────────────────
  // Prefill if editing
  // ───────────────────────────────
  useEffect(() => {
    if (editSnapshotData) {
      console.log("🟡 Editing existing Brand Snapshot:", editSnapshotData);

      setIsEditing(true);
      setStep(3); // go directly to review step

      snapshotConfigRef.current.snapshotId = editSnapshotData._id || editSnapshotData.snapshotId;
      snapshotConfigRef.current.snapshotName = editSnapshotData.snapshot_name || editSnapshotData.snapshotName;

      // ✅ Handle recipient_emails from backend vs email field in config
      if (Array.isArray(editSnapshotData.recipient_emails)) {
        snapshotConfigRef.current.email = editSnapshotData.recipient_emails.join(", ");
      } else {
        snapshotConfigRef.current.email = editSnapshotData.recipient_emails || editSnapshotData.email || "";
      }
      snapshotConfigRef.current.scheduleMonthly = editSnapshotData.scheduleMonthly;

      if (editSnapshotData.primaryProfile) {
        setSelectedPrimaryProfile(editSnapshotData.primaryProfile);
      }
      if (Array.isArray(editSnapshotData.secondaryProfiles)) {
        setSelectedSecondaryProfiles(editSnapshotData.secondaryProfiles);
      }
    }
  }, [editSnapshotData]);

  // ───────────────────────────────
  // Navbar height & scroll effects
  // ───────────────────────────────
  useEffect(() => {
    const navbarEl = document.querySelector("header.MuiAppBar-root");
    if (navbarEl) setNavHeight(navbarEl.offsetHeight);

    const resizeObserver = new ResizeObserver(() => {
      if (navbarEl) setNavHeight(navbarEl.offsetHeight);
    });
    if (navbarEl) resizeObserver.observe(navbarEl);
    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    const handleScroll = () => setIsTransparent(window.scrollY === 0);
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // ───────────────────────────────
  // OAuth callback handling
  // ───────────────────────────────
  useEffect(() => {
    const intent = localStorage.getItem("postOAuthIntent");
    const confirmed = localStorage.getItem("postOAuthConfirmed");

    if (intent === "brand" && confirmed === "true") {
      console.log("🔁 OAuth: refreshing primary profiles...");
      localStorage.removeItem("postOAuthIntent");
      localStorage.removeItem("postOAuthConfirmed");

      setShowConnectDialog(false);
      setTimeout(() => {
        primaryProfileRef.current?.refreshProfiles?.();
      }, 350);
    }
  }, []);

  // ───────────────────────────────
  // Main Action Handler
  // ───────────────────────────────
  const handleContinue = async () => {
    if (step === 1) return setStep(2);
    if (step === 2) return setStep(3);

    if (step === 3) {
      // 🛡️ [VALIDATION] Ensure emails are provided if immediate send is checked
      const emailsArr = snapshotConfigRef.current.email
        ?.split(",")
        .map((e) => e.trim())
        .filter(Boolean);

      if ((snapshotConfigRef.current.sendSnapshotImmediately || snapshotConfigRef.current.scheduleMonthly) && (!emailsArr || emailsArr.length === 0)) {
        setShowEmailError(true);
        return;
      }

      setIsSaving(true);
      try {
        const rangeStart = dayjs.utc().subtract(1, "month").startOf("month").toDate();
        const rangeEnd = dayjs.utc().subtract(1, "month").endOf("month").toDate();
        const nextRunAt = snapshotConfigRef.current.scheduleMonthly
          ? dayjs.utc().endOf("month").add(1, "day").startOf("day").toDate()
          : null;

        const snapshotInfo = {
          snapshotType: "Brand",
          snapshotName: snapshotConfigRef.current.snapshotName,
          emails: snapshotConfigRef.current.email
            ?.split(",")
            .map((e) => e.trim())
            .filter(Boolean),
          primaryProfile: {
            profileId: selectedPrimaryProfile?.id,
            platform: selectedPrimaryProfile?.platform,
          },
          secondaryProfiles: selectedSecondaryProfiles.map((p) => ({
            profileId: p.id,
            platform: p.platform,
          })),
          primaryEntities: [],
          secondaryEntities: [],
          frequency: snapshotConfigRef.current.scheduleMonthly ? "monthly" : "once",
          scheduleEnabled: snapshotConfigRef.current.scheduleMonthly,
          rangeStart,
          rangeEnd,
          nextRunAt,
          branding_pack_id: snapshotConfigRef.current.branding_pack_id || null, // [FIX] Include selected branding pack
          target_client: snapshotConfigRef.current.target_client || null,        // [FIX] Include target client
        };

        const existingSnapshotId = snapshotConfigRef.current.snapshotId;

        if (isEditing && existingSnapshotId) {
          console.log(`🟡 Updating existing Brand Snapshot metadata: ${existingSnapshotId}`);
          await WorkspaceService.updateWorkspaceSnapshot(existingSnapshotId, snapshotInfo);
          console.log("✅ Brand Snapshot metadata updated successfully");
        } else {
          console.log("🆕 Creating new Brand Snapshot...");
          if (!isLiveDemo) {
            await WorkspaceService.saveWorkspaceSnapshot(snapshotInfo);
            console.log("✅ Brand Snapshot saved successfully");
          } else {
            console.log("⚠️ Live Demo: Downloading Brand Snapshot PDF locally instead of saving");
            await SnapshotService.generateBrandSnapshot(snapshotInfo);
          }
        }

        // 📧 Handle Immediate Email Dispatch [NEW]
        console.log("🔍 [DEBUG] sendSnapshotImmediately:", snapshotConfigRef.current.sendSnapshotImmediately);
        if (snapshotConfigRef.current.sendSnapshotImmediately) {
          console.log("🚀 [DEBUG] User requested immediate snapshot email. Payload:", { snapshotInfo, emails: snapshotInfo.emails });
          try {
            const emailRes = await SnapshotService.emailSnapshot(snapshotInfo, snapshotInfo.emails);
            console.log("✅ [DEBUG] Immediate snapshot email response:", emailRes);
          } catch (emailErr) {
            console.error("⚠️ [DEBUG] Immediate snapshot email failed:", emailErr);
          }
        }

        onComplete();
      } catch (err) {
        console.error("❌ Failed to save/update Brand Snapshot:", err);
      } finally {
        setIsSaving(false);
      }
    }
  };

  const renderErrorSB = (
    <MDSnackbar
      color="error"
      icon="warning"
      title="Validation Error"
      content={errorMessage}
      open={errorSB}
      onClose={closeErrorSB}
      close={closeErrorSB}
      bgWhite
    />
  );

  // ───────────────────────────────
  // Back Navigation
  // ───────────────────────────────
  const handleBack = () => {
    if (isEditing) {
      onComplete(); // go back to snapshot list
      return;
    }

    if (step === 1) return goBack();

    // Clear inline error if going back from review step
    if (step === 3) setShowEmailError(false);

    setStep(step - 1);
  };

  const handleClear = () => {
    if (step === 1) setSelectedPrimaryProfile(null);
    if (step === 2) setSelectedSecondaryProfiles([]);
  };

  // ───────────────────────────────
  // Selected Profiles
  // ───────────────────────────────
  const selectedProfiles =
    step === 1
      ? selectedPrimaryProfile
        ? [selectedPrimaryProfile]
        : []
      : step === 2
        ? selectedSecondaryProfiles
        : [selectedPrimaryProfile, ...selectedSecondaryProfiles];

  // ───────────────────────────────
  // Loading State - [MOVED TO BUTTON LEVEL]
  // ───────────────────────────────

  // ───────────────────────────────
  // Render UI
  // ───────────────────────────────
  return (
    <MDBox textAlign="center" mt={-5}>
      {/* Sticky Stepper Toolbar */}
      <MDBox
        display="flex"
        alignItems="center"
        justifyContent="flex-start"
        width="100%"
        bgcolor="white"
        py={1}
        px={1}
        sx={{
          position: "sticky",
          top: `${navHeight - 1}px`,
          zIndex: 999,
          backgroundColor: "#ffffff",
          borderRadius: "8px",
          gap: 2,
          mb: 1,
          boxShadow: isTransparent ? "none" : "0 4px 12px rgba(0, 0, 0, 0.2)",
          transition: "top 0.3s ease, background-color 0.3s ease, box-shadow 0.3s ease",
        }}
      >
        <IconButton onClick={handleBack} sx={{ color: "primary.main", ml: 1 }}>
          <ArrowBackIcon />
        </IconButton>

        <MDBox flexGrow={1} pl={2}>
          <SnapshotStepper steps={steps} activeStep={step} />
        </MDBox>
      </MDBox>

      {/* Step 1: Select Primary Profile */}
      {step === 1 && (
        <SelectPrimaryProfile
          ref={primaryProfileRef}
          selectedProfileId={selectedPrimaryProfile?.id}
          onSelect={(profile) => setSelectedPrimaryProfile(profile)}
          onAddProfileClick={() => setShowConnectDialog(true)}
        />
      )}

      {/* Step 2: Select Secondary Profiles */}
      {step === 2 && (
        <SelectSecondaryProfile
          selectedProfiles={selectedSecondaryProfiles}
          onSelect={(profiles) => setSelectedSecondaryProfiles(profiles)}
          excludeIds={selectedPrimaryProfile ? [selectedPrimaryProfile.id] : []}
          filterPlatform={selectedPrimaryProfile?.platform}
          maxSelection={2}
          isDemoHighlight={enableDemoHighlight} // [NEW]
        />
      )}

      {/* Step 3: Review & Generate / Save Snapshot */}
      {step === 3 && (
        <MDBox pt={6} pb={3}>
          <ReviewAndGenerateSnapshot
            snapshotType="Brand"
            primaryProfile={selectedPrimaryProfile}
            secondaryProfiles={selectedSecondaryProfiles}
            snapshotConfigRef={snapshotConfigRef}
            snapshotId={snapshotConfigRef.current.snapshotId || null}
            entitlements={entitlements}
            forceEmailError={showEmailError} // [NEW]
            onSave={handleContinue}
            onCancel={handleBack}
            isSaving={isSaving}
            demoHighlight={enableDemoHighlight} // [NEW]
            isLiveDemo={isLiveDemo} // [NEW]
          />
        </MDBox>
      )}

      {/* Floating Action Popup (Hidden in Step 3 review to avoid double buttons) */}
      {step < 3 && (
        <SocialPopup
          selectedItems={selectedProfiles}
          onClearConfirm={step < 3 ? handleClear : undefined}
          onContinue={handleContinue}
          onBack={handleBack}
          actionLabel={
            step === 3 ? "Save Snapshot" : "Continue"
          }
          entityType="profile"
          isEditing={isEditing}
          isSaving={isSaving} // 🟢 Pass saving state
          demoHighlight={
            enableDemoHighlight &&
            ((step === 1 && !!selectedPrimaryProfile) || (step === 2 && selectedSecondaryProfiles.length > 0) || step === 3)
          }
          allowDemoAction={true}
        />
      )}

      {/* Connect Profile Dialog */}
      <ConnectPrimaryProfile
        open={showConnectDialog}
        onClose={() => setShowConnectDialog(false)}
        flowIntent="brand"
      />
      {renderErrorSB}
    </MDBox>
  );
}

export default BrandSnapshot;
